import {
    Mesh,
    MeshPhysicalMaterial,
    MeshStandardMaterial,
    Texture,
    type BufferGeometry,
    type Group,
    type Material,
} from 'three'
import { copyVariantMaterial } from './copyVariantMaterial'

type MaterialSelection = Material | Material[]
type MaterialMesh = Mesh<BufferGeometry, MaterialSelection>

/**
 * Tracks every mutable Three resource owned by one mounted GLB instance.
 *
 * Geometry and decoded texture sources remain shared through Drei's cache;
 * materials and mutable texture settings belong exclusively to this instance.
 */
export default class OwnedGLBResources {
    private disposed = false
    private readonly materials = new Map<Material, Material>()
    private readonly ownedMaterials = new Set<Material>()
    private readonly ownedTextures = new Set<Texture>()
    private readonly textures = new Map<Texture, Texture>()

    public ownMaterial(source:Material):Material {
        if(this.ownedMaterials.has(source)) return source

        const existing = this.materials.get(source)
        if(existing) return existing

        const owned = source.clone()
        this.materials.set(source, owned)
        this.ownedMaterials.add(owned)
        this.ownMaterialTextures(source, owned)

        if(owned instanceof MeshStandardMaterial) {
            owned.envMapIntensity = 1
            owned.toneMapped = false
        }

        return owned
    }

    public ownMaterialSelection(
        source:MaterialSelection,
    ):MaterialSelection {
        return Array.isArray(source)
            ? source.map(material => this.ownMaterial(material))
            : this.ownMaterial(source)
    }

    public createMaterialCopy(source:Material):Material {
        // Both standard and physical PBR variants need the same display
        // object: accumulated shadows retain and restore that exact object.
        const owned = source instanceof MeshStandardMaterial
            ? new MeshPhysicalMaterial()
            : source.clone()
        copyVariantMaterial(owned, source)
        this.ownedMaterials.add(owned)
        this.ownMaterialTextures(source, owned)
        return owned
    }

    public ownTexture(source:Texture):Texture {
        if(this.ownedTextures.has(source)) return source

        const existing = this.textures.get(source)
        if(existing) return existing

        // Texture.clone() preserves the decoded Source/image but gives the
        // viewer independent repeat, offset, wrapping, filtering, and flipY.
        const owned = source.clone()
        this.textures.set(source, owned)
        this.ownedTextures.add(owned)
        return owned
    }

    public isDisposed():boolean {
        return this.disposed
    }

    public dispose(scene:Group) {
        if(this.disposed) return
        this.disposed = true

        // Include materials installed later by product adapters. By invariant,
        // every material currently on this scene is instance-owned.
        scene.traverse(object => {
            if(!(object instanceof Mesh)) return

            const mesh = object as MaterialMesh
            const materials = Array.isArray(mesh.material)
                ? mesh.material
                : [mesh.material]
            materials.forEach(material => {
                this.ownedMaterials.add(material)
            })
        })

        this.ownedMaterials.forEach(material => material.dispose())
        this.ownedTextures.forEach(texture => texture.dispose())
        this.materials.clear()
        this.ownedMaterials.clear()
        this.ownedTextures.clear()
        this.textures.clear()
    }

    private ownMaterialTextures(source:Material, owned:Material) {
        const sourceProperties = (
            source as unknown as Record<string, unknown>
        )
        const ownedProperties = (
            owned as unknown as Record<string, unknown>
        )

        Object.entries(sourceProperties).forEach(([property, value]) => {
            if(value instanceof Texture) {
                ownedProperties[property] = this.ownTexture(value)
            }
        })
    }
}
